import { useEffect, useCallback, useRef } from 'react';
import { useNotifications } from '../hooks/useNotifications';

export const NotificationManager = () => {
  const { preferences, hasPermission, checkExpiringItems } = useNotifications();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckRef = useRef<string | null>(null);

  const performNotificationCheck = useCallback(async () => {
    // Only check if user has notifications enabled and has granted permission
    if (!preferences?.expiry_notifications_enabled || !hasPermission) {
      return;
    }

    const now = new Date();
    const today = now.toDateString();

    // Only check once per day to avoid spam
    if (lastCheckRef.current === today) {
      return;
    }

    try {
      // Check if it's within the user's notification time window
      if (preferences.notification_time) {
        const userTime = new Date();
        const [hours, minutes] = preferences.notification_time.split(':').map(Number);
        const targetTime = new Date();
        targetTime.setHours(hours, minutes, 0, 0);

        // Check if it's within 1 hour of the target time
        const timeDiff = Math.abs(userTime.getTime() - targetTime.getTime());
        const oneHour = 60 * 60 * 1000;

        if (timeDiff <= oneHour) {
          console.log('🔔 Checking for expiring items...');
          await checkExpiringItems();
          lastCheckRef.current = today;
        }
      }
    } catch (error) {
      console.error('Failed to check expiring items:', error);
    }
  }, [preferences, hasPermission, checkExpiringItems]);

  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Only set up interval if notifications are enabled
    if (preferences?.expiry_notifications_enabled && hasPermission) {
      // Check every 30 minutes
      intervalRef.current = setInterval(performNotificationCheck, 30 * 60 * 1000);
      
      // Also check immediately if we haven't checked today
      const today = new Date().toDateString();
      if (lastCheckRef.current !== today) {
        performNotificationCheck();
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [preferences?.expiry_notifications_enabled, hasPermission, performNotificationCheck]);

  // Also check when the page becomes visible again (user returns to app)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && preferences?.expiry_notifications_enabled && hasPermission) {
        const today = new Date().toDateString();
        if (lastCheckRef.current !== today) {
          performNotificationCheck();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [preferences?.expiry_notifications_enabled, hasPermission, performNotificationCheck]);

  // This component doesn't render anything - it just manages background tasks
  return null;
}; 